import type { FC, ReactNode } from 'react';

export interface StatsCardProps {
  title: string;
  value: string | number;
  icon: ReactNode;
  color?: 'primary' | 'success' | 'warning' | 'error' | 'purple';
  trend?: {
    value: string;
    direction: 'up' | 'down' | 'neutral';
  };
  isLoading?: boolean;
  className?: string;
}

const colorClasses = {
  primary: 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400',
  success: 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400',
  warning: 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400',
  error: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400',
  purple: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
};

const trendClasses = {
  up: 'text-green-600 dark:text-green-400',
  down: 'text-red-600 dark:text-red-400',
  neutral: 'text-gray-500 dark:text-slate-400',
};

const trendIcons = {
  up: '\u2191',
  down: '\u2193',
  neutral: '\u2192',
};

export const StatsCard: FC<StatsCardProps> = ({
  title,
  value,
  icon,
  color = 'primary',
  trend,
  isLoading = false,
  className = '',
}) => {
  const baseClasses =
    'backdrop-blur-sm bg-white/90 dark:bg-slate-800/90 rounded-xl shadow-sm dark:shadow-slate-900/50 border border-primary-100/50 dark:border-slate-700 p-4 hover:shadow-md transition-shadow';

  if (isLoading) {
    return (
      <div className={`${baseClasses} ${className}`}>
        <div className="animate-pulse flex items-center gap-3">
          <div className="h-10 w-10 bg-gray-200 dark:bg-slate-700 rounded-xl" />
          <div className="space-y-2">
            <div className="h-4 w-20 bg-gray-200 dark:bg-slate-700 rounded" />
            <div className="h-6 w-16 bg-gray-200 dark:bg-slate-700 rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${baseClasses} ${className}`}>
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-xl ${colorClasses[color]}`}>{icon}</div>
        <div>
          <p className="text-sm text-gray-600 dark:text-slate-400">{title}</p>
          <p className="text-xl font-semibold text-gray-900 dark:text-slate-100">{value}</p>
          {trend && (
            <div className={`text-xs mt-1 ${trendClasses[trend.direction]}`}>
              {trendIcons[trend.direction]} {trend.value}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
